import api from './api';

export const getReferralDiscount = async (): Promise<{ discountPercent: number; partnerCommissionPercent: number; promoCodes: string[] }> => {
    const response = await api.get('/referral/discount');
    return response.data;
};

export const setReferralDiscount = async (
    discountPercent: number,
    partnerCommissionPercent: number,
    promoCodes?: string[]
): Promise<any> => {
    const response = await api.put('/referral/discount', { discountPercent, partnerCommissionPercent, promoCodes });
    return response.data;
};

export interface AdminUser {
    _id: string;
    name: string;
    lastName?: string;
    email: string;
    role: 'student' | 'sde' | 'admin' | 'ep' | 'parent';
    subscriptionStatus?: 'free' | 'active' | 'canceled' | 'past_due';
    createdAt: string;
    updatedAt: string;
}

export interface AdminUsersResponse {
    users: AdminUser[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
    query: {
        search: string;
    };
}

export const getAdminUsers = async (params: { page?: number; limit?: number; search?: string }): Promise<AdminUsersResponse> => {
    const response = await api.get('/user/admin/users', { params });
    return response.data;
};

export const updateStudentSubscriptionStatus = async (userId: string, subscribed: boolean): Promise<{
    message: string;
    user: {
        _id: string;
        subscriptionStatus: 'free' | 'active' | 'canceled' | 'past_due';
        updatedAt: string;
    };
}> => {
    const response = await api.patch(`/user/admin/users/${userId}/subscription`, { subscribed });
    return response.data;
};

export interface AdminPartnerListItem {
    id: string;
    name: string;
    lastName: string;
    fullName: string;
    email: string;
    createdAt: string;
    stripeConnectAccountId: string | null;
    codeStats: {
        totalCodes: number;
        totalCodeUses: number;
        totalUsersReferred: number;
        totalEarned: number;
        lastCodeCreatedAt: string | null;
    };
    withdrawalStats: {
        totalRequests: number;
        totalWithdrawn: number;
        pendingAmount: number;
        rejectedAmount: number;
        cancelledAmount: number;
        lastWithdrawalAt: string | null;
    };
    balance: {
        available: number;
    };
}

export interface AdminPartnersResponse {
    partners: AdminPartnerListItem[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNextPage: boolean;
        hasPrevPage: boolean;
    };
    query: {
        search: string;
    };
}

export interface AdminPartnerUsage {
    code: string;
    user: { id: string; name: string; email: string } | null;
    subscriptionAmount: number;
    commission: number;
    planName: string;
    date: string | null;
}

export interface AdminPartnerCodeDetail {
    id: string;
    code: string;
    totalEarned: number;
    usageCount: number;
    createdAt: string;
    usages: Array<{
        user: { id: string; name: string; email: string } | null;
        subscriptionAmount: number;
        commission: number;
        planName: string;
        date: string | null;
    }>;
}

export interface AdminPartnerWithdrawalDetail {
    id: string;
    amount: number;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    payoutMethod: 'upi' | 'bank_transfer';
    payoutDetails: {
        upiId?: string;
        accountHolderName?: string;
        accountNumber?: string;
        ifscCode?: string;
    };
    note?: string;
    rejectionReason?: string;
    paymentReference?: string;
    processedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface AdminPartnerDetailsResponse {
    partner: {
        id: string;
        name: string;
        lastName: string;
        fullName: string;
        email: string;
        createdAt: string;
        stripeConnectAccountId: string | null;
    };
    summary: {
        totalCodes: number;
        totalCodeUses: number;
        totalUsersReferred: number;
        totalEarned: number;
        totalWithdrawn: number;
        pendingAmount: number;
        availableBalance: number;
        totalWithdrawRequests: number;
        rejectedAmount: number;
        cancelledAmount: number;
        lastUsageAt: string | null;
        lastWithdrawalAt: string | null;
    };
    codes: AdminPartnerCodeDetail[];
    recentUsages: AdminPartnerUsage[];
    withdrawals: AdminPartnerWithdrawalDetail[];
    referredUsers: Array<{ id: string; name: string; email: string }>;
}

export const getAdminPartners = async (params: { page?: number; limit?: number; search?: string }): Promise<AdminPartnersResponse> => {
    const response = await api.get('/referral/admin/partners', { params });
    return response.data;
};

export const getAdminPartnerDetails = async (epId: string): Promise<AdminPartnerDetailsResponse> => {
    const response = await api.get(`/referral/admin/partners/${epId}`);
    return response.data;
};
